import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { ApiResponse, apiClient, apiClientNoAuth } from "./_client";

export interface Notification {
  id: string;
  seen: boolean;
  category: string;
  message: string;
  meta: {
    portfolio_id?: string;
    project_id?: string;
    instance_id?: string;
  };
  timestamp: string;
}

export interface Notifications {
  unseen_count: number;
  notifications: Notification[];
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_staff: boolean;
  email_verified: boolean;
  is_overseer: boolean;
}

export const useUser = (force = false): UseQueryResult<User> => {
  const q = useQuery<User>({
    queryKey: ["currentUser"],
    queryFn: async () => {
      let url = "api/users/me/";
      if (force) {
        url += "?force_refresh_token=true";
      }
      const response = await apiClient.get<never, AxiosResponse<User>>(url);
      return response.data;
    },
    staleTime: 60 * 1000,
  });
  return q;
};

export const useNotifications = (): UseQueryResult<Notifications> => {
  const q = useQuery({
    queryKey: ["notifications"],
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await apiClient.get("api/users/me/notifications/");
      return response.data;
    },
  });
  return q;
};

export const markNotificationAsRead = async (ids: string[]) => {
  if (ids.length === 0) {
    return;
  }
  const response = await apiClient.post("api/users/me/notifications/", {
    ids,
  });
  return response;
};

export const updateUser = async (user: Partial<User>): Promise<ApiResponse<User>> => {
  const response = await apiClient.put<never, ApiResponse<User>>(
    "api/users/me/",
    user,
  );
  return response;
};

export const requestVerificationEmail = async () => {
  const response = await apiClient.post("api/users/resend_verification_email/");
  return response;
};

export const verifyEmail = async (uid: string, token: string) => {
  const response = await apiClient.post(`api/users/verify/${uid}/${token}/`, {
    uid,
    token,
  });
  return response;
};

export const requestPasswordReset = async (
  email: string,
): Promise<ApiResponse> => {
  const response = await apiClientNoAuth.post("api/users/password_reset/", {
    email,
  });
  return response;
};

// PW Reset API will return 200 for failed resets, so we need to handle
// this different vs. regular 4xx and 5xx api responses.
type PWError = {
  password: string;
};
type PWSuccess = { ok: true };

export const resetPassword = async (
  uidb64: string,
  token: string,
  password: string,
): Promise<ApiResponse<PWSuccess, PWError>> => {
  const response = await apiClientNoAuth.put<
    never,
    ApiResponse<PWSuccess, PWError>
  >(`api/users/password_reset/`, {
    uidb64,
    token,
    password,
  });
  return response;
};

export const requestEmailUpdate = async (
  new_email: string,
): Promise<ApiResponse> => {
  const response = await apiClient.post("api/users/request_email_update/", {
    new_email,
  });
  return response;
};
